import React, { useState, useCallback, useEffect, useRef } from 'react';
import axiosInstance from '../config/axios';
import './MissingItemTicketsPanel.css';

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch (_) {
    return iso;
  }
};

const StatusBadge = ({ status }) => {
  const isOpen = status === 'open';
  return (
    <span className={`mitp-badge ${isOpen ? 'mitp-badge-open' : 'mitp-badge-closed'}`}>
      {isOpen ? 'Open' : 'Closed'}
    </span>
  );
};

const MissingItemTicketsPanel = ({
  tickets = [],
  mode = 'site',
  loading = false,
  onRefresh,
  onCloseTicket,
  photoBasePath
}) => {
  const [expandedId, setExpandedId] = useState(null);
  const [outcomeDrafts, setOutcomeDrafts] = useState({});
  const [closingId, setClosingId] = useState(null);
  const [closeError, setCloseError] = useState('');
  const [lightbox, setLightbox] = useState(null);
  const objectUrlRef = useRef(null);

  useEffect(() => {
    return () => {
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
    };
  }, []);

  const toggleExpand = (id) => {
    setExpandedId((prev) => (prev === id ? null : id));
    setCloseError('');
  };

  const openPhoto = useCallback(async (ticket) => {
    if (!photoBasePath) return;
    setLightbox({ url: null, loading: true, error: '' });
    try {
      const res = await axiosInstance.get(`${photoBasePath}/${ticket.id}/photo`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = url;
      setLightbox({ url, loading: false, error: '' });
    } catch (err) {
      setLightbox({ url: null, loading: false, error: 'Failed to load photo.' });
    }
  }, [photoBasePath]);

  const closeLightbox = () => {
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
    setLightbox(null);
  };

  const handleOutcomeChange = (id, value) => {
    setOutcomeDrafts((prev) => ({ ...prev, [id]: value }));
  };

  const handleCloseTicket = async (ticket) => {
    const outcome = (outcomeDrafts[ticket.id] || '').trim();
    if (!outcome) {
      setCloseError('An outcome is required to close this ticket.');
      return;
    }
    setCloseError('');
    setClosingId(ticket.id);
    try {
      await onCloseTicket?.(ticket, outcome);
      setOutcomeDrafts((prev) => {
        const next = { ...prev };
        delete next[ticket.id];
        return next;
      });
      setExpandedId(null);
    } catch (err) {
      setCloseError(err?.message || 'Failed to close ticket.');
    } finally {
      setClosingId(null);
    }
  };

  if (loading) {
    return <div className="mitp-loading">Loading tickets…</div>;
  }

  return (
    <div className="mitp-panel">
      {onRefresh && (
        <div className="mitp-toolbar">
          <button type="button" className="btn btn-secondary mitp-refresh-btn" onClick={onRefresh}>Refresh</button>
        </div>
      )}
      {!tickets.length ? (
        <div className="mitp-empty">
          <p>No inventory tickets{mode === 'admin' ? '' : ' yet'}.</p>
        </div>
      ) : (
        <div className="mitp-list">
          {tickets.map((ticket) => {
            const isExpanded = expandedId === ticket.id;
            const isClosed = ticket.status === 'closed';
            const typeLabel = ticket.ticketTypeLabel
              || (ticket.ticketType === 'item_change_request' ? 'Item change request' : 'Missing item');
            return (
              <div key={ticket.id} className={`mitp-card${isExpanded ? ' mitp-card-expanded' : ''}`}>
                <button type="button" className="mitp-card-header" onClick={() => toggleExpand(ticket.id)}>
                  <div className="mitp-card-header-main">
                    <span className="mitp-card-desc">{ticket.itemDescription}</span>
                    <span className="mitp-card-type">{typeLabel}</span>
                    {mode === 'admin' && ticket.siteName && (
                      <span className="mitp-card-site">{ticket.siteName}</span>
                    )}
                    {mode === 'admin' && ticket.brandName && (
                      <span className="mitp-card-site">{ticket.brandName}</span>
                    )}
                  </div>
                  <div className="mitp-card-header-meta">
                    <span className="mitp-card-date">{formatDate(ticket.createdAt)}</span>
                    <StatusBadge status={ticket.status} />
                  </div>
                </button>
                {isExpanded && (
                  <div className="mitp-card-body">
                    {mode === 'admin' && (
                      <div className="mitp-field">
                        <span className="mitp-field-label">Submitted by</span>
                        <p className="mitp-field-value">
                          {(ticket.submittedByRole === 'manager' ? 'Manager' : 'Site')}
                          {ticket.submittedByName ? ` — ${ticket.submittedByName}` : ''}
                        </p>
                      </div>
                    )}
                    {ticket.inventoryItemName && (
                      <div className="mitp-field">
                        <span className="mitp-field-label">Selected item</span>
                        <p className="mitp-field-value">{ticket.inventoryItemName}</p>
                      </div>
                    )}
                    {ticket.requestedChange && (
                      <div className="mitp-field">
                        <span className="mitp-field-label">Requested change</span>
                        <p className="mitp-field-value">{ticket.requestedChange}</p>
                      </div>
                    )}
                    {ticket.notes && (
                      <div className="mitp-field">
                        <span className="mitp-field-label">Notes</span>
                        <p className="mitp-field-value">{ticket.notes}</p>
                      </div>
                    )}
                    {ticket.hasPhoto && (
                      <div className="mitp-field">
                        <span className="mitp-field-label">Photo</span>
                        <button type="button" className="btn btn-secondary mitp-view-photo-btn" onClick={() => openPhoto(ticket)}>
                          View Photo
                        </button>
                      </div>
                    )}
                    {isClosed ? (
                      <div className="mitp-field">
                        <span className="mitp-field-label">Outcome</span>
                        <p className="mitp-field-value">{ticket.adminOutcome || '—'}</p>
                        <span className="mitp-field-sub">Closed {formatDate(ticket.closedAt)}</span>
                      </div>
                    ) : (
                      mode === 'admin' && (
                        <div className="mitp-close-form">
                          <label htmlFor={`mitp-outcome-${ticket.id}`} className="mitp-field-label">Outcome (required to close)</label>
                          <textarea
                            id={`mitp-outcome-${ticket.id}`}
                            className="mitp-outcome-input"
                            rows={3}
                            value={outcomeDrafts[ticket.id] || ''}
                            onChange={(e) => handleOutcomeChange(ticket.id, e.target.value)}
                            placeholder="Describe how this was resolved…"
                          />
                          {closeError && <div className="mitp-close-error">{closeError}</div>}
                          <button
                            type="button"
                            className="btn btn-primary mitp-close-btn"
                            onClick={() => handleCloseTicket(ticket)}
                            disabled={closingId === ticket.id}
                          >
                            {closingId === ticket.id ? 'Closing…' : 'Close Ticket'}
                          </button>
                        </div>
                      )
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      {lightbox && (
        <div className="mitp-lightbox-overlay" onClick={closeLightbox} role="dialog" aria-modal="true">
          <div className="mitp-lightbox" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="mitp-lightbox-close" onClick={closeLightbox} aria-label="Close">&times;</button>
            {lightbox.loading && <p className="mitp-lightbox-status">Loading photo…</p>}
            {lightbox.error && <p className="mitp-lightbox-status mitp-lightbox-error">{lightbox.error}</p>}
            {lightbox.url && <img src={lightbox.url} alt="Inventory ticket" className="mitp-lightbox-img" />}
          </div>
        </div>
      )}
    </div>
  );
};

export default MissingItemTicketsPanel;
